import React from 'react'
import { Link } from 'react-router-dom'
import { FileText, ArrowLeft, Calendar, CheckCircle, ArrowRight } from 'lucide-react'
import { PublicFooter } from '../../components/layout/PublicFooter'
import { PublicNavigation } from '../../components/layout/PublicNavigation'
import { Button } from '../../components/ui/Button'

export const ExchangeProcessPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 relative overflow-hidden transition-colors duration-300">
      <PublicNavigation />

      {/* Hero Section */}
      <section className="py-20 lg:py-32 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Link to="/documentation" className="inline-flex items-center text-[#69adba] hover:text-[#5a9aa6] dark:hover:text-[#7bb8c5] mb-6">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Documentation
            </Link>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#69adba]/10 dark:bg-[#69adba]/20 rounded-full mb-6">
              <FileText className="h-8 w-8 text-[#69adba]" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-6xl mb-6">
              Exchange Process Guide
            </h1>
            <p className="mt-6 text-lg leading-8 text-slate-600 dark:text-slate-400">
              Detailed guides on completing each phase of your 1031 exchange.
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {/* Phase One */}
              <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center">
                    <span className="text-[#69adba] font-bold text-xl">1</span>
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                    Phase One: Relinquished Property Sale
                  </h2>
                </div>
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                  The first phase involves documenting the sale of your relinquished property. 
                  Collect all required information and documents.
                </p>
                <ul className="space-y-2 text-slate-600 dark:text-slate-400">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#69adba] mr-2 mt-0.5 flex-shrink-0" />
                    <span>Exchanger's names and contact information</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#69adba] mr-2 mt-0.5 flex-shrink-0" />
                    <span>Buyer's names for notice purposes</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#69adba] mr-2 mt-0.5 flex-shrink-0" />
                    <span>Closing company details and closer information</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#69adba] mr-2 mt-0.5 flex-shrink-0" />
                    <span>Property address or legal description</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#69adba] mr-2 mt-0.5 flex-shrink-0" />
                    <span>Banking subaccount information (if applicable)</span>
                  </li>
                </ul>
              </div>

              {/* Phase Two */}
              <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center">
                    <span className="text-[#69adba] font-bold text-xl">2</span>
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                    Phase Two: Replacement Property Purchase
                  </h2>
                </div>
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                  The second phase involves documenting the purchase of your replacement property. 
                  Ensure all information is accurate and complete.
                </p>
                <ul className="space-y-2 text-slate-600 dark:text-slate-400">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#69adba] mr-2 mt-0.5 flex-shrink-0" />
                    <span>Exchanger's names and contact information</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#69adba] mr-2 mt-0.5 flex-shrink-0" />
                    <span>Seller's names for notice purposes</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#69adba] mr-2 mt-0.5 flex-shrink-0" />
                    <span>Closing company details and closer information</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#69adba] mr-2 mt-0.5 flex-shrink-0" />
                    <span>Replacement property address or legal description</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#69adba] mr-2 mt-0.5 flex-shrink-0" />
                    <span>Disbursement form with wire transfer details</span>
                  </li>
                </ul>
              </div>

              {/* Deadlines */}
              <div className="bg-gradient-to-br from-[#69adba]/10 to-[#69adba]/20 dark:from-[#69adba]/20 dark:to-[#69adba]/30 p-8 rounded-2xl border border-[#69adba]/20 dark:border-[#69adba]/30">
                <div className="flex items-center space-x-3 mb-4">
                  <Calendar className="h-6 w-6 text-[#69adba]" />
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                    Important Deadlines
                  </h2>
                </div>
                <div className="space-y-3 text-slate-700 dark:text-slate-300">
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">45-Day Identification Period</h3>
                    <p>You must identify potential replacement properties within 45 days of closing on your relinquished property.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">180-Day Exchange Period</h3>
                    <p>You must close on your replacement property within 180 days of closing on your relinquished property.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#69adba] dark:bg-[#5a9aa6]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl mb-4">
            Ready to Start Your Exchange?
          </h2>
          <p className="mt-4 text-lg text-white/90 mb-8">
            Use the Exchange Hub to guide you through each phase step by step.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              className="bg-white text-[#69adba] hover:bg-slate-50"
              onClick={() => {
                window.dispatchEvent(new CustomEvent('openChat'))
              }}
            >
              Start Exchange
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Link to="/documentation">
              <Button variant="outline" className="border-white text-white hover:bg-white/10">
                View All Documentation
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  )
}

